import React, { useState, useEffect, useMemo } from "react";
import { ChevronDown, ChevronUp, Save, X } from "lucide-react";
import { FaEdit } from "react-icons/fa";
import { cn } from "@/lib/utils";
import { mapUIParamsToBackend, mergeBenchmarkParamsWithTemplate } from "../data/sopDataByFormat";

export default function BenchmarkCard({
  sopData,
  recipe,
  recipeFormat,
  onSaveSpecificFields,
  onChange,
  isFinalized = false,
  isExpanded: controlledExpanded,
  onToggleExpand,
}) {
  const isControlled = controlledExpanded !== undefined;
  const [localExpanded, setLocalExpanded] = useState(false);
  const isExpanded = isControlled ? controlledExpanded : localExpanded;

  // Completely INDEPENDENT edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const initialComment =
    sopData?.benchmark?.comment ??
    sopData?.benchmark?.sensoryFeedback ??
    recipe?.benchmarkSensoryFeedback ??
    "";

  const initialRemark =
    sopData?.benchmark?.remark ??
    recipe?.benchmarkRemark ??
    "";

  const initialOthers =
    sopData?.others ??
    recipe?.benchmarkOthers ??
    "";

  const initialPDFeedback =
    sopData?.productDevelopmentFeedback ??
    recipe?.benchmarkPDFeedback ??
    "";

  const formatKey = String(
    recipeFormat ||
    recipe?.recipeFormat ||
    recipe?.recipeType ||
    recipe?.format ||
    ""
  ).toLowerCase();

  const initialAnalyticalReport = useMemo(() => {
    const rawReport =
      sopData?.parameters?.analyticalReport?.length > 0
        ? sopData.parameters.analyticalReport
        : (Array.isArray(recipe?.benchmarkParameters) && recipe.benchmarkParameters.length > 0
            ? recipe.benchmarkParameters
            : []);

    return mergeBenchmarkParamsWithTemplate(rawReport, formatKey);
  }, [sopData?.parameters?.analyticalReport, recipe?.benchmarkParameters, formatKey]);

  const [draftComment, setDraftComment] = useState(initialComment);
  const [draftRemark, setDraftRemark] = useState(initialRemark);
  const [draftOthers, setDraftOthers] = useState(initialOthers);
  const [draftPDFeedback, setDraftPDFeedback] = useState(initialPDFeedback);
  const [draftAnalyticalReport, setDraftAnalyticalReport] = useState(initialAnalyticalReport);

  useEffect(() => {
    setDraftComment(initialComment);
    setDraftRemark(initialRemark);
    setDraftOthers(initialOthers);
    setDraftPDFeedback(initialPDFeedback);
    setDraftAnalyticalReport(initialAnalyticalReport);
  }, [sopData, recipe, initialAnalyticalReport]);

  useEffect(() => {
    if (!isExpanded && isEditing) {
      setIsEditing(false);
    }
  }, [isExpanded, isEditing]);

  const handleToggle = (e) => {
    e?.stopPropagation?.();
    const nextState = !isExpanded;
    if (isExpanded && isEditing) {
      handleCancelLocal(e);
    }
    if (isControlled) {
      onToggleExpand?.(nextState);
    } else {
      setLocalExpanded(nextState);
    }
  };

  const handleStartEdit = (e) => {
    e?.stopPropagation?.();
    setDraftComment(initialComment);
    setDraftRemark(initialRemark);
    setDraftOthers(initialOthers);
    setDraftPDFeedback(initialPDFeedback);
    setDraftAnalyticalReport(initialAnalyticalReport);
    if (!isExpanded) {
      if (isControlled) {
        onToggleExpand?.(true);
      } else {
        setLocalExpanded(true);
      }
    }
    setIsEditing(true);
  };

  const handleSaveLocal = async (e) => {
    e?.stopPropagation?.();
    try {
      setIsSaving(true);
      if (onSaveSpecificFields) {
        await onSaveSpecificFields({
          benchmarkSensoryFeedback: draftComment,
          benchmarkRemark: draftRemark,
          benchmarkParameters: mapUIParamsToBackend(draftAnalyticalReport),
          benchmarkOthers: draftOthers,
          benchmarkPDFeedback: draftPDFeedback,
        });
      }

      if (onChange) {
        onChange({
          ...sopData,
          benchmark: {
            ...(sopData?.benchmark || {}),
            comment: draftComment,
            remark: draftRemark,
            sensoryFeedback: draftComment,
          },
          parameters: {
            ...(sopData?.parameters || {}),
            analyticalReport: draftAnalyticalReport,
          },
          others: draftOthers,
          productDevelopmentFeedback: draftPDFeedback,
        });
      }

      setIsEditing(false);
    } catch (err) {
      console.error("Failed to save Benchmark:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelLocal = (e) => {
    e?.stopPropagation?.();
    setDraftComment(initialComment);
    setDraftRemark(initialRemark);
    setDraftOthers(initialOthers);
    setDraftPDFeedback(initialPDFeedback);
    setDraftAnalyticalReport(initialAnalyticalReport);
    setIsEditing(false);
  };

  const handleAnalyticalParamChange = (label, value) => {
    setDraftAnalyticalReport((prev) =>
      prev.map((p) => (p.label === label ? { ...p, value } : p))
    );
  };

  return (
    <div
      onClick={!isExpanded ? handleToggle : undefined}
      className={cn(
        "flex flex-col bg-white dark:bg-[#0D0B14] border border-[#EEEBF4] dark:border-primary/40 rounded-2xl lg:rounded-lg xl:rounded-xl 2xl:rounded-xl 3xl:rounded-2xl p-6 lg:p-3 xl:p-4 2xl:p-5 3xl:p-6 shadow-sm transition-all h-full",
        !isExpanded && "cursor-pointer hover:border-[#4B208B]/40 dark:hover:border-[#4B208B]/60"
      )}
    >
      {/* Header */}
      <div
        onClick={handleToggle}
        className="flex items-center justify-between cursor-pointer select-none group"
      >
        <h2 className="text-[22px] lg:text-[12px] xl:text-[15px] 2xl:text-[18px] 3xl:text-[22px] font-semibold text-[#0D111A] dark:text-white tracking-tight group-hover:text-[#4B208B] dark:group-hover:text-purple-400 transition-colors">
          Benchmark
        </h2>

        <div className="flex items-center gap-3 lg:gap-1.5 xl:gap-2 2xl:gap-2.5 3xl:gap-3" onClick={(e) => e.stopPropagation()}>
          {/* Edit / Save / Cancel Controls (visible only when expanded) */}
          {isExpanded && (
            isEditing ? (
              <div className="flex items-center overflow-hidden rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl bg-[#4B208B] text-white shadow-sm">
                <button
                  type="button"
                  onClick={handleSaveLocal}
                  disabled={isSaving}
                  title="Save Changes"
                  className="flex items-center justify-center w-9 lg:w-5 xl:w-6 2xl:w-7.5 3xl:w-9 h-9 lg:h-5 xl:h-6 2xl:h-7.5 3xl:h-9 hover:bg-[#3E1B77] transition-colors disabled:opacity-50 cursor-pointer"
                >
                  <Save className="w-4 lg:w-2 xl:w-2.5 2xl:w-3.5 3xl:w-4 h-4 lg:h-2 xl:h-2.5 2xl:h-3.5 3xl:h-4" />
                </button>
                <div className="w-px h-5 lg:h-3 xl:h-3.5 2xl:h-4 3xl:h-5 bg-white/30" />
                <button
                  type="button"
                  onClick={handleCancelLocal}
                  disabled={isSaving}
                  title="Cancel Changes"
                  className="flex items-center justify-center w-9 lg:w-5 xl:w-6 2xl:w-7.5 3xl:w-9 h-9 lg:h-5 xl:h-6 2xl:h-7.5 3xl:h-9 hover:bg-[#3E1B77] transition-colors disabled:opacity-50 cursor-pointer"
                >
                  <X className="w-4 lg:w-2 xl:w-2.5 2xl:w-3.5 3xl:w-4 h-4 lg:h-2 xl:h-2.5 2xl:h-3.5 3xl:h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleStartEdit}
                disabled={isFinalized}
                title="Edit Benchmark"
                className="flex items-center justify-center w-9 lg:w-5 xl:w-6 2xl:w-7.5 3xl:w-9 h-9 lg:h-5 xl:h-6 2xl:h-7.5 3xl:h-9 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl bg-[#4B208B] hover:bg-[#3E1B77] text-white shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                <FaEdit className="w-3.5 lg:w-2 xl:w-2.5 2xl:w-3 3xl:w-3.5 h-3.5 lg:h-2 xl:h-2.5 2xl:h-3 3xl:h-3.5" />
              </button>
            )
          )}

          {/* Chevron expand/collapse toggle */}
          <button
            type="button"
            onClick={handleToggle}
            title={isExpanded ? "Collapse" : "Expand"}
            className="flex items-center justify-center w-8 lg:w-4.5 xl:w-5.5 2xl:w-6.5 3xl:w-8 h-8 lg:h-4.5 xl:h-5.5 2xl:h-6.5 3xl:h-8 rounded-lg lg:rounded-sm xl:rounded-md 2xl:rounded-md 3xl:rounded-lg text-gray-500 group-hover:text-gray-900 dark:text-gray-400 dark:group-hover:text-white transition-colors cursor-pointer"
          >
            {isExpanded ? (
              <ChevronUp className="w-5 lg:w-2.5 xl:w-3.5 2xl:w-4 3xl:w-5 h-5 lg:h-2.5 xl:h-3.5 2xl:h-4 3xl:h-5" />
            ) : (
              <ChevronDown className="w-5 lg:w-2.5 xl:w-3.5 2xl:w-4 3xl:w-5 h-5 lg:h-2.5 xl:h-3.5 2xl:h-4 3xl:h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Expanded Content (matching Figma) */}
      {isExpanded && (
        <div className="flex flex-col gap-6 lg:gap-3 xl:gap-4 2xl:gap-5 3xl:gap-6 pt-6 lg:pt-3 xl:pt-4 2xl:pt-5 3xl:pt-6 border-t border-[#EEEBF4] dark:border-primary/40 mt-4 lg:mt-2 xl:mt-2.5 2xl:mt-3 3xl:mt-4 flex-1">
          {/* 1. Benchmark Analysis */}
          <div className="flex flex-col gap-2.5 lg:gap-1 xl:gap-1.5 2xl:gap-2 3xl:gap-2.5">
            <h3 className="text-sm lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-sm font-semibold text-gray-900 dark:text-white tracking-tight">
              Benchmark Analysis
            </h3>

            {/* Compact 2-column table with vertical divider (matching Figma) */}
            <div className="w-full max-w-[280px] lg:max-w-[200px] xl:max-w-[240px] 2xl:max-w-[260px] 3xl:max-w-sm border border-[#D9D9D9] dark:border-primary/30 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl overflow-hidden bg-white dark:bg-[#121019]">
              {draftAnalyticalReport.map((param, index) => (
                <div
                  key={index}
                  className="flex items-center text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs divide-x divide-[#D9D9D9] dark:divide-primary/30 border-b last:border-b-0 border-[#D9D9D9] dark:border-primary/30"
                >
                  <div className="w-1/2 px-3.5 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-3.5 py-2 lg:py-1 xl:py-1 2xl:py-1.5 3xl:py-2 text-gray-800 dark:text-gray-200 font-normal">
                    {param.label}
                  </div>
                  <div className="w-1/2 px-3.5 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-3.5 py-2 lg:py-1 xl:py-1 2xl:py-1.5 3xl:py-2 text-gray-900 dark:text-white font-normal flex items-center">
                    {isEditing ? (
                      <input
                        type="text"
                        value={param.value}
                        onChange={(e) =>
                          handleAnalyticalParamChange(param.label, e.target.value)
                        }
                        className="w-full px-2 lg:px-1 xl:px-1.5 2xl:px-1.5 3xl:px-2 py-0.5 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs rounded border border-[#4B208B] bg-white dark:bg-[#151221] text-gray-900 dark:text-white focus:outline-none"
                      />
                    ) : (
                      <span>
                        {param.value !== undefined && param.value !== null && String(param.value).trim() !== ""
                          ? `${param.value} ${param.unit || ""}`.trim()
                          : "-"}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 2. Sensory Feedback & Feedback Fields (matching Figma) */}
          <div className="flex flex-col gap-3 lg:gap-1.5 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
            <h3 className="text-sm lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-sm font-semibold text-gray-900 dark:text-white tracking-tight">
              Sensory Feedback
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 lg:gap-x-4 xl:gap-x-5 2xl:gap-x-6 3xl:gap-x-8 gap-y-5 lg:gap-y-2.5 xl:gap-y-3.5 2xl:gap-y-4 3xl:gap-y-5">
              {/* Comment */}
              <div className="flex flex-col gap-1.5 lg:gap-0.5 xl:gap-1 2xl:gap-1 3xl:gap-1.5">
                <label className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-normal text-gray-500 dark:text-gray-400">
                  Comment
                </label>
                {isEditing ? (
                  <textarea
                    rows={3}
                    value={draftComment}
                    onChange={(e) => setDraftComment(e.target.value)}
                    className="w-full p-3 lg:p-1.5 xl:p-2 2xl:p-2.5 3xl:p-3 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl border-2 border-[#4B208B] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs leading-relaxed bg-white dark:bg-[#151221] text-gray-800 dark:text-gray-200 resize-none focus:outline-none ring-1 ring-[#4B208B] shadow-sm"
                  />
                ) : (
                  <p className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs leading-relaxed text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                    {draftComment || <span className="text-gray-400">-</span>}
                  </p>
                )}
              </div>

              {/* Remark */}
              <div className="flex flex-col gap-1.5 lg:gap-0.5 xl:gap-1 2xl:gap-1 3xl:gap-1.5">
                <label className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-normal text-gray-500 dark:text-gray-400">
                  Remark
                </label>
                {isEditing ? (
                  <textarea
                    rows={3}
                    value={draftRemark}
                    onChange={(e) => setDraftRemark(e.target.value)}
                    className="w-full p-3 lg:p-1.5 xl:p-2 2xl:p-2.5 3xl:p-3 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl border-2 border-[#4B208B] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs leading-relaxed bg-white dark:bg-[#151221] text-gray-800 dark:text-gray-200 resize-none focus:outline-none ring-1 ring-[#4B208B] shadow-sm"
                  />
                ) : (
                  <p className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs leading-relaxed text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                    {draftRemark || <span className="text-gray-400">-</span>}
                  </p>
                )}
              </div>

              {/* Others */}
              <div className="flex flex-col gap-1.5 lg:gap-0.5 xl:gap-1 2xl:gap-1 3xl:gap-1.5">
                <label className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-normal text-gray-500 dark:text-gray-400">
                  Others
                </label>
                {isEditing ? (
                  <textarea
                    rows={3}
                    value={draftOthers}
                    onChange={(e) => setDraftOthers(e.target.value)}
                    className="w-full p-3 lg:p-1.5 xl:p-2 2xl:p-2.5 3xl:p-3 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl border-2 border-[#4B208B] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs leading-relaxed bg-white dark:bg-[#151221] text-gray-800 dark:text-gray-200 resize-none focus:outline-none ring-1 ring-[#4B208B] shadow-sm"
                  />
                ) : (
                  <p className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs leading-relaxed text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                    {draftOthers || <span className="text-gray-400">-</span>}
                  </p>
                )}
              </div>

              {/* Product Development Feedback */}
              <div className="flex flex-col gap-1.5 lg:gap-0.5 xl:gap-1 2xl:gap-1 3xl:gap-1.5">
                <label className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-normal text-gray-500 dark:text-gray-400">
                  Product Development Feedback
                </label>
                {isEditing ? (
                  <textarea
                    rows={3}
                    value={draftPDFeedback}
                    onChange={(e) => setDraftPDFeedback(e.target.value)}
                    className="w-full p-3 lg:p-1.5 xl:p-2 2xl:p-2.5 3xl:p-3 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl border-2 border-[#4B208B] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs leading-relaxed bg-white dark:bg-[#151221] text-gray-800 dark:text-gray-200 resize-none focus:outline-none ring-1 ring-[#4B208B] shadow-sm"
                  />
                ) : (
                  <p className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs leading-relaxed text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                    {draftPDFeedback || <span className="text-gray-400">-</span>}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
